/**
 * NoteKeyResolver — ensures the note key for a given note is available
 * in memory (noteKeyStore).
 *
 * Resolution order:
 *  1. Return from noteKeyStore if already present.
 *  2. Fetch the wrapper from the backend.
 *  3. Unwrap it with the masterKey.
 *  4. Store the result in noteKeyStore.
 *
 * If masterKey is not in memory the caller must handle that separately
 * (e.g. open the unlock modal) before calling this function.
 */

import { useNoteKeyStore } from '@/store/noteKeyStore'
import { useMasterPasswordStore } from '@/store/masterPasswordStore'
import { apiGetNoteKeyWrapper } from '@/services/NoteKeyWrapperService'
import { initSodium, unwrapNoteKey } from '@/services/CryptoService'

export type ResolveResult =
    | { ok: true;  noteKey: Uint8Array }
    | { ok: false; reason: 'no-master-key' | 'no-wrapper' | 'unwrap-failed' | 'network-error' }

/**
 * Ensure the note key for `noteId` is in memory and return it.
 *
 * This is a pure async function (not a hook) so it can be called from
 * event handlers, effects, or other async flows.
 *
 * @param noteId - UUID of the note.
 * @returns      - `{ ok: true, noteKey }` on success, or `{ ok: false, reason }` on failure.
 */
export async function resolveNoteKey(noteId: string): Promise<ResolveResult> {
    // 1. Check in-memory store first.
    const cached = useNoteKeyStore.getState().getNoteKey(noteId)
    if (cached) {
        return { ok: true, noteKey: cached }
    }

    // 2. masterKey must be available to unwrap.
    const masterKey = useMasterPasswordStore.getState().masterKey
    if (!masterKey) {
        return { ok: false, reason: 'no-master-key' }
    }

    // 3. Fetch the wrapper from the backend.
    let wrapperData: { wrapped_key: string; nonce: string; alg: string }
    try {
        wrapperData = await apiGetNoteKeyWrapper(noteId)
    } catch (err: any) {
        const status = err?.response?.status
        if (status === 404) {
            return { ok: false, reason: 'no-wrapper' }
        }
        return { ok: false, reason: 'network-error' }
    }

    // 4. Unwrap the note key.
    try {
        await initSodium()
        const noteKey = unwrapNoteKey(wrapperData.wrapped_key, wrapperData.nonce, masterKey)

        // 5. Store in noteKeyStore (store takes ownership).
        useNoteKeyStore.getState().setNoteKey(noteId, noteKey)

        return { ok: true, noteKey }
    } catch {
        return { ok: false, reason: 'unwrap-failed' }
    }
}

